import { useMemo } from 'react';
import { RowData } from '@tanstack/react-table';

import { IconButton, IconButtonSize } from '@xemplo/icon-button';
import {
  DawnArrowLeft,
  DawnArrowLeftEnd,
  DawnArrowRight,
  DawnArrowRightEnd,
} from '@xemplo/icons';
import { Item, TextDropdown } from '@xemplo/text-dropdown';

import { formatNumber } from '../table.helper';
import { TableProps } from '../table.types';

import {
  defaultPageSizeOptions,
  iconButtonIds,
  PaginationTestId,
} from './pagination.constants';
import * as S from './pagination.style';
import { PaginationProps } from './pagination.types';
import { usePageSizeOptions } from './use-page-size-options';

export function Pagination<TData extends RowData>({
  table,
  pageSizeOptions = defaultPageSizeOptions,
}: Readonly<PaginationProps<TData>>) {
  const { pagination } = table.getState();
  const {
    enablePagination,
    paginationThreshold = 10,
    manualPagination,
    totalRowCount,
    isLoading,
  } = table.options as TableProps<TData>;

  const coreRowLength = table.getCoreRowModel().rows.length;

  const shouldPaginate = useMemo(() => {
    if (!enablePagination || isLoading) return false;
    if (manualPagination && totalRowCount) {
      return totalRowCount > paginationThreshold;
    }
    return coreRowLength > paginationThreshold;
  }, [
    enablePagination,
    isLoading,
    manualPagination,
    totalRowCount,
    paginationThreshold,
    coreRowLength,
  ]);

  const allowedPageSizeOptions = usePageSizeOptions(pageSizeOptions);

  if (!shouldPaginate) return null;

  return (
    <S.Wrapper data-testid={PaginationTestId.wrapper}>
      <S.ItemCounter table={table} testId={PaginationTestId.counter} />
      <S.Controls data-testid={PaginationTestId.controls}>
        <IconButton
          id={iconButtonIds.firstPage}
          ariaLabel="First page"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
          naked
          size={IconButtonSize.Small}
        >
          <DawnArrowLeftEnd fontSize={16} />
        </IconButton>
        <IconButton
          id={iconButtonIds.previousPage}
          ariaLabel="Previous page"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          naked
          size={IconButtonSize.Small}
        >
          <DawnArrowLeft fontSize={16} />
        </IconButton>
        <span data-testid={PaginationTestId.pages}>
          {formatNumber({ value: pagination.pageIndex + 1 })} of{' '}
          {formatNumber({ value: table.getPageCount() })}
        </span>
        <IconButton
          id={iconButtonIds.nextPage}
          ariaLabel="Next page"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          naked
          size={IconButtonSize.Small}
        >
          <DawnArrowRight fontSize={16} />
        </IconButton>
        <IconButton
          id={iconButtonIds.lastPage}
          ariaLabel="Last page"
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
          naked
          size={IconButtonSize.Small}
        >
          <DawnArrowRightEnd fontSize={16} />
        </IconButton>
      </S.Controls>
      <S.PageSizeSelector>
        <span>Show</span>
        <TextDropdown
          aria-label="Page size selector"
          defaultSelectedKey={pagination.pageSize.toString()}
          defaultItems={allowedPageSizeOptions}
          testId={PaginationTestId.pageSizeSelector}
          onInputChange={(e) => table.setPageSize(Number(e))}
          width="100px"
        >
          {allowedPageSizeOptions.map((pageSize) => (
            <Item key={pageSize.key} textValue={pageSize.value}>
              {pageSize.value}
            </Item>
          ))}
        </TextDropdown>
      </S.PageSizeSelector>
    </S.Wrapper>
  );
}
